import { Injectable } from '@angular/core';
import { LoggedInUser } from '../models/user.model';

const USER_KEY = 'cms_user';
const ROLE_KEY = 'cms_role';
const EMAIL_KEY = 'cms_email';
const TOKEN_KEY = 'cms_token';
const REFRESH_TOKEN_KEY = 'cms_refresh_token';

@Injectable({
  providedIn: 'root',
})
export class Storage {

  setUser(user: LoggedInUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (user.role) {
      localStorage.setItem(ROLE_KEY, user.role);
    }
    if (user.email) {
      localStorage.setItem(EMAIL_KEY, user.email);
    }
    if (user.token) {
      localStorage.setItem(TOKEN_KEY, user.token);
    }
    if (user.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, user.refreshToken);
    }
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
    const user = this.getUser();
    if (user) {
      user.token = token;
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  }

  setRefreshToken(refreshToken: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    const user = this.getUser();
    if (user) {
      user.refreshToken = refreshToken;
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  }

  getToken(): string | null {
    const directToken = localStorage.getItem(TOKEN_KEY);
    if (directToken) return directToken;
    const user = this.getUser();
    return user?.token || null;
  }

  getRefreshToken(): string | null {
    const directRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (directRefresh) return directRefresh;
    const user = this.getUser();
    return user?.refreshToken || null;
  }

  getUser(): LoggedInUser | null {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as LoggedInUser) : null;
  }

  getRole(): string | null {
    return localStorage.getItem(ROLE_KEY);
  }

  getEmail(): string | null {
    return localStorage.getItem(EMAIL_KEY);
  }

  clear(): void {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  isTokenExpired(token?: string | null): boolean {
    const targetToken = token ?? this.getToken();
    if (!targetToken) return true;
    try {
      const parts = targetToken.split('.');
      if (parts.length !== 3) return true;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      if (!decoded.exp) return false;
      // Buffer of 10 seconds for clock skew
      return Date.now() >= (decoded.exp * 1000 - 10000);
    } catch {
      return true;
    }
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();
    if (!token && !refreshToken) return false;

    if (token && !this.isTokenExpired(token)) {
      return true;
    }

    if (refreshToken && !this.isTokenExpired(refreshToken)) {
      return true;
    }

    this.clear();
    return false;
  }
}

